import { ChangeDetectionStrategy, Component, computed, inject, output, signal, WritableSignal } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { DatePickerService, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { WalletTypeEnum } from '@olv/ui-shared/models/wallet.model';
import { TradeTypeEnum, TransactionTypeEnum } from '@olv/ui-shared/models/merchant-transaction.model';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

const ALL_TRADE_TYPES_VALUE = 0;
const ALL_TRADE_TYPE_VALUES = [TradeTypeEnum.Buy, TradeTypeEnum.Sell];

export interface ConfigDropdown {
  key: string;
  placeholder: string;
  label: string;
  options: { value: string; label: string }[];
  type: 'default' | 'checkbox';
  isSearch?: boolean;
  isFilterClass?: boolean;
  afterFilter?: string;
}

interface ConfigDateFilter {
  key: string;
  label: string;
  value?: string;
  start?: Date | null;
  end?: Date | null;
}

interface SelectedValues<T = string | number> {
  name: string;
  values: T[];
}

interface Filters {
  name: string;
  filterFields: { name: string; value: number }[];
}

interface FiltersDialogData {
  filters?: Filters[];
  dropdownsOptions?: ConfigDropdown[];
  preselectedValues?: SelectedValues[];
  dateFiltersOptions?: ConfigDateFilter[];
  isDisableFilters?: boolean;
}

@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [DropdownComponent, FormsModule, DatePickerComponent, MatIconModule, ButtonComponent],
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<FiltersDialogComponent>);
  private readonly data = inject<FiltersDialogData>(MAT_DIALOG_DATA);
  private readonly datePickerService = inject(DatePickerService);
  readonly resetRequest = output<[]>();
  isResetEmited = signal(false);
  isDisableFilters = signal(this.data?.isDisableFilters ?? false);

  readonly filters: Filters[] = this.data?.filters ?? [];
  readonly dropdownsOptions: WritableSignal<ConfigDropdown[]> = signal(
    this.data?.dropdownsOptions?.length ? this.data.dropdownsOptions : []
  );
  readonly isPooledWalletSelected = computed(() => {
    const walletTypeSet = this.selectedCheckboxes().get('Wallet Type');
    return walletTypeSet?.has(WalletTypeEnum.Pooled) ?? false;
  });
  readonly isExchangeTransactionTypeSelected = computed(() => {
    const transactionTypeSet = this.selectedCheckboxes().get('Transaction Type');
    return transactionTypeSet?.has(TransactionTypeEnum.Exchange) ?? false;
  });
  readonly selectedCheckboxes = signal<Map<string, Set<number>>>(new Map());
  readonly selectedDropdowns = signal<Map<string, string[]>>(new Map());
  readonly dateFilters = signal<ConfigDateFilter[]>(
    (this.data?.dateFiltersOptions ?? []).map(data => ({ ...data, start: data.start ?? null, end: data.end ?? null }))
  );

  constructor() {
    this.initializeState();
  }

  readonly isApplyDisabled = computed(() => {
    const hasCheckbox = Array.from(this.selectedCheckboxes().values()).some((set) => set.size > 0);
    const hasDropdown = Array.from(this.selectedDropdowns().values()).some((vals) => vals.length > 0);
    const hasDate = this.dateFilters().some((data) => data.start && data.end);

    const hadValuesAtStart = !!this.data.preselectedValues?.some((v) => v.values?.length > 0);
    const isCurrentlyEmpty = !(hasCheckbox || hasDropdown || hasDate);

    if(this.isDisableFilters()) return true;

    if (!this.isFiltersChanged() && !this.isResetEmited()) return true;

    if (isCurrentlyEmpty && !hadValuesAtStart) return true;

    return this.isResetEmited() && isCurrentlyEmpty;
  });

  get applyState(): 'disabled' | 'default' {
    return this.isApplyDisabled() ? 'disabled' : 'default';
  }

  readonly topDropdowns = computed(() =>
    this.dropdownsOptions().filter(
      (dd) => !dd.afterFilter || !this.filters.some((filter) => filter.name === dd.afterFilter),
    ),
  );

  dropdownsAfter(filterName: string): ConfigDropdown[] {
    return this.dropdownsOptions().filter(
      (dd) => dd.afterFilter === filterName && this.filters.some((filter) => filter.name === dd.afterFilter),
    );
  }

  readonly dropdownModels = computed(() => {
    const map = new Map<string, string | string[]>();

    for (const dd of this.dropdownsOptions()) {
      const vals = this.selectedDropdowns().get(dd.key) ?? [];

      if (dd.type === 'checkbox') {
        map.set(dd.key, vals);
      } else {
        map.set(dd.key, vals[0] ?? '');
      }
    }

    return map;
  });

  readonly isFiltersChanged = computed(() => {
    const normalize = (items: SelectedValues[]) =>
      items
        .filter((i) => {
          if (i.name !== 'Trade Type') return true;

          const values = new Set(i.values.map(Number));
          return !ALL_TRADE_TYPE_VALUES.every(value => values.has(value));
        })
        .map((i) => ({ name: i.name, values: [...i.values].sort() }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const current: SelectedValues[] = [];

    this.selectedCheckboxes().forEach((set, name) => {
      if (set.size) current.push({ name, values: [...set] });
    });

    this.selectedDropdowns().forEach((values, key) => {
      if (values.length) current.push({ name: key, values: values });
    });

    this.dateFilters().forEach((d) => {
      if (d.start && d.end) {
        current.push({
          name: d.key,
          values: [d.start.toISOString(), d.end.toISOString()],
        });
      }
    });

    return JSON.stringify(normalize(current)) !== JSON.stringify(normalize(this.data.preselectedValues ?? []));
  });

  readonly isResetRequested = computed(() => {
    return !!this.data.preselectedValues?.length && !this.isResetEmited();
  });

  private initializeState(): void {
    const preselected: SelectedValues[] = this.data?.preselectedValues ?? [];
    if (!preselected.length) return;

    const dropdownMap = new Map<string, string[]>();
    for (const dd of this.dropdownsOptions()) {
      const entry = preselected.find(item => item.name === dd.key);
      if (entry) dropdownMap.set(dd.key, entry.values as string[]);
    }
    this.selectedDropdowns.set(dropdownMap);

    this.dateFilters.update(dates =>
      dates.map(dateFilter => {
        const entry = preselected.find(item => item.name === dateFilter.key);
        if (entry && entry.values.length === 2) {
          return {
            ...dateFilter,
            start: new Date(entry.values[0]),
            end: new Date(entry.values[1])
          };
        }
        return dateFilter;
      })
    );

    const newMap = new Map<string, Set<number>>();
    this.filters.forEach(filter => {
      const entry = preselected.find(item => item.name === filter.name);
      if (entry) {
        const selectedValues = entry.values as number[];
        const values = new Set(
          filter.name === 'Trade Type'
            ? selectedValues.filter(value => value !== ALL_TRADE_TYPES_VALUE)
            : selectedValues
        );

        if (
          filter.name === 'Trade Type' &&
          ALL_TRADE_TYPE_VALUES.every(value => values.has(value))
        ) {
          return;
        }

        if (values.size > 0) {
          newMap.set(filter.name, values);
        }
      }
    });
    this.selectedCheckboxes.set(newMap);
  }

  onDateRangeSelected(range: RangeOfDate, key: string): void {
    const selectedRange = this.datePickerService.dateRangeSelected(range);

    this.dateFilters.update(dates =>
      dates.map(date => date.key === key ? { ...date, start: selectedRange.start, end: selectedRange.end } : date)
    );
  }

  toggleCheckbox(filterName: string, value: number): void {
    this.selectedCheckboxes.update(map => {
      const newMap = new Map(map);
      const currentSet = new Set(newMap.get(filterName) || []);

      if (currentSet.has(value)) {
        currentSet.delete(value);
      } else {
        currentSet.add(value);
      }

      if(currentSet.size > 0) {
        newMap.set(filterName, currentSet);
      } else {
        newMap.delete(filterName);
      }

      if (filterName === 'Wallet Type' && !currentSet.has(WalletTypeEnum.Pooled)) {
        newMap.delete('Pool Wallet Type');
      }

      if (filterName === 'Transaction Type' && !currentSet.has(TransactionTypeEnum.Exchange)) {
        newMap.delete('Trade Type');
      }

      if (filterName === 'Trade Type') {
        if (value === ALL_TRADE_TYPES_VALUE) {
          newMap.delete('Trade Type');
          return newMap;
        }

        currentSet.delete(ALL_TRADE_TYPES_VALUE);

        if (currentSet.size > 0) {
          newMap.set(filterName, currentSet);
        } else {
          newMap.delete(filterName);
        }
      }

      return newMap;
    });
  }

  shouldShowFilter(filterName: string): boolean {
    if (filterName === 'Pool Wallet Type') {
      return this.isPooledWalletSelected();
    }

    if (filterName === 'Trade Type') {
      return this.isExchangeTransactionTypeSelected();
    }

    return true;
  }

  isChecked(filterName: string, value: number): boolean {
    if (filterName === 'Trade Type' && value === ALL_TRADE_TYPES_VALUE) {
      return this.isExchangeTransactionTypeSelected() && !this.selectedCheckboxes().has(filterName);
    }

    return this.selectedCheckboxes().get(filterName)?.has(value) ?? false;
  }

  onDropdownChange(key: string, values: string[]): void {
    this.selectedDropdowns.update((map) => {
      const next = new Map(map);

      if (values.length) {
        next.set(key, values);
      } else {
        next.delete(key);
      }

      return next;
    });
  }

  onDropdownModelChange(dd: ConfigDropdown, model: string | string[]): void {
    const values =
      dd.type === 'checkbox'
        ? ((model as string[]) ?? [])
        : model
          ? [model as string]
          : [];

    this.onDropdownChange(dd.key, values);
  }

  onReset(): void {
    this.selectedCheckboxes.set(new Map());
    this.selectedDropdowns.set(new Map());
    this.dateFilters.update(dates => dates.map(date => ({
      ...date,
      start: null,
      end: null,
    })));

    if (this.isResetRequested()) {
      this.isResetEmited.set(true);
      this.resetRequest.emit([]);
    }
  }

  onApply(): void {
    const result: SelectedValues[] = [];

    this.selectedCheckboxes().forEach((set, name) => {
      if (set.size > 0 && this.shouldShowFilter(name)) {
        result.push({
          name: name,
          values: Array.from(set)
        });
      }
    });

    if (this.shouldShowFilter('Trade Type') && !this.selectedCheckboxes().has('Trade Type')) {
      result.push({ name: 'Trade Type', values: ALL_TRADE_TYPE_VALUES });
    }

    this.selectedDropdowns().forEach((values, key) => {
      if (values.length) result.push({ name: key, values: values });
    });

    this.dateFilters().forEach(date => {
      if (date.start && date.end) {
        result.push({
          name: date.key,
          values: [date.start.toISOString(), date.end.toISOString()],
        });
      }
    });

    this.dialogRef.close(result);
  }

  updateDropdownOptions(key: string, options: { value: string; label: string }[]): void {
    this.dropdownsOptions.update(dropdowns =>
      dropdowns.map(dd => dd.key === key ? { ...dd, options } : dd)
    );
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
